<template>
    <div
        rating
        question
        :id="`question-${questionData.number}`"
        @keyup.enter="submitQuestion"
    >
        <div
            v-if="questionData.settings.showLabels "
            data-selected-label
        >
            <p
                v-html="selectedAnswerText"
                :id="`question-${questionData.number}_label`"
            ></p>
        </div>
        <questionheader :questionData="questionData" />
        <div data-question-main>
            <div
                :disabled="outerFieldIsChecked"
                :id="`question-${questionData.number}_graph-${questionData.number}`"
            >
                <strong data-only-screenreader>
                    {{ get.textResource(`rs_options`) }}
                </strong>
                <section
                    v-if="graphType === `rectangle`"
                    data-rating-stars
                    role="radiogroup"
                    :class="{ compact: segments.length < 6 }"
                    :aria-label="questionData.question"
                >
                    <button
                        v-for="(answer, index) in segments"
                        :key="answer.id"
                        type="button"
                        data-star
                        :id="`rectangle-question-${questionData.number}_answer-${answer.number}`"
                        :class="{ active: selectedSegments >= index + 1 }"
                        :disabled="editingAllowed"
                        :aria-label="answer.label"
                        :aria-pressed="selectedSegments >= index + 1"
                        @click="segmentClick(index)"
                        @mouseenter="segmentMouseenter(index)"
                        @mouseleave="segmentMouseenter(-1)"
                    >
                        <svg width="48px" height="48px" viewBox="0 -0.5 33 33" 
                            version="1.1" xmlns="http://www.w3.org/2000/svg" 
                            xmlns:xlink="http://www.w3.org/1999/xlink" 
                            class="svg-inline--fa fa-star fa-w-18"
                            aria-hidden="true">
                            <g stroke="none" stroke-width="1">
                                <g transform="translate(-903.000000, -411.000000)">
                                    <g id="Icons" transform="translate(37.000000, 169.000000)">
                                        <g id="star" transform="translate(858.000000, 234.000000)">
                                            <g transform="translate(7.000000, 8.000000)" id="Shape">
                                                <polygon points="27.865 31.83 17.615 26.209 7.462 32.009 9.553 20.362 0.99 12.335 12.532 10.758 17.394 0 22.436 10.672 34 12.047 25.574 20.22">
                                                </polygon>
                                            </g>
                                        </g>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    </button>
                </section>
            </div>
            <checkboxradio
                v-if="outerField"
                :questionData="questionData"
                :outerfield="true"
                :answerData="outerField"
                :showLabel="true"
                :disabled="!get.editingAllowed(questionData, false) || state.global.showLoader"
                type="checkbox"
                @element-click="outerFieldClicked"
            />
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";

export default {
    name: `rating-question`,
    mixins: [mixins.defaultQuestion],
    inject: ['DOMPurify'],

    data() {
        return {
            graphType: `rectangle`,
            hoveredIndex: -1,
        };
    },

    computed: {
        answersGiven() {
            return this.questionData.answers.filter(
                (answer) => answer.isChecked
            ).length;
        },
        segments() {
            return this.questionData.answers.filter(
                answer => answer.type === `None`
            );
        },
        outerField() {
            return this.questionData.answers.find(
                answer => answer.type === `OuterField`
            );
        },
        selectedSegments() {
            if (this.hoveredIndex !== -1) {
                return this.hoveredIndex + 1;
            }

            const checkedIndex = this.segments.findIndex(
                answer => answer.isChecked
            );

            return checkedIndex === -1 ? 0 : checkedIndex + 1;
        },
        selectedAnswerText() {
            if(this.selectedAnswer === undefined) {
                return this.DOMPurify.sanitize(this.get.textResource(`rs_rating_selected_label`) + ` -`);
            }
            else {
                return this.DOMPurify.sanitize(this.get.textResource(`rs_rating_selected_label`) + ` ` + this.selectedAnswer?.label);
            }  
        },
        selectedAnswer() {
            return this.segments[this.selectedSegments - 1];
        },
        editingAllowed() {
            return (
                !this.get.editingAllowed(this.questionData) ||
                this.state.global.showLoader
            );
        },
        outerFieldIsChecked() {
            return (
                this.outerField !== undefined &&
                this.outerField.isChecked
            );
        },
    },
    methods: {
        segmentClick(segmentIndex) {
            this.checkSegments(segmentIndex);

            if (this.outerField) {
                this.outerField.isChecked = false;
            }

            this.$store.dispatch(`autoNext`, {
                questionData: this.questionData
            });
        },
        segmentMouseenter(segmentIndex) {
            this.hoveredIndex = segmentIndex;
        },
        checkSegments(segmentIndex) {
            this.segments.forEach((answer, index) => {
                answer.isChecked = index === segmentIndex;
            });
        },
        outerFieldClicked() {
            this.outerField.isChecked =
                !this.outerField.isChecked;

            if (this.outerField.isChecked) {
                this.segments.forEach((answer) => {
                    answer.isChecked = false;
                });
            }

            this.$store.dispatch(`autoNext`, {
                questionData: this.questionData
            });
        },
    },
};
</script>

<style scoped>
[data-question-main] {
    width: 100%;
}

[data-rating-stars] {
    --star-size: 44px;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
    width: 100%;
    margin: 20px 0;
    justify-items: center;
    align-items: center;
}

[data-rating-stars].compact {
    grid-template-columns: repeat(auto-fit, var(--star-size));
    justify-content: center;
}

[data-star] {
    width: var(--star-size);
    height: var(--star-size);
    border: none;
    background: transparent;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform .15s ease;
    &[disabled] {
        &.active {
            svg {
                fill: #a7a4a4;
            }
        }
        svg {
            fill: #e4e4e4;
        }
    }

}

[data-star] svg {
    width: 100%;
    height: 100%;
    fill: #d8cedc;
    color: #d8cedc;
    transition: fill .15s ease;
}

[data-star].active svg {
    fill: var(--primary-color);
    color: var(--primary-color);
}

[data-selected-label] {
    display: flex;
    justify-content: flex-end;
    padding: 5px 40px 5px 35px;
    p {
        color: var(--QuestionStyling-color);
        font-weight: 600;
        font-size: var(--AnswerStyling-font-size);
        margin-block-end: 0;
    }
}

@media (min-width: 768px) {
    [data-rating-stars] {
        --star-size: 64px;
        gap: 12px;
    }
}

@media (min-width: 1024px) {
    [data-rating-stars] {
        --star-size: 84px;
        gap: 16px;
    }
}
</style>